import { useMemo } from 'react'

import { queryView, useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  PLATFORM_WINDOWS,
  ResourcePickerScreen,
  grantsOf,
  platformMyResources,
  type ResourcePickerActions,
} from '@krp/platform-ui'
import { AQSI_WINDOWS } from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { tileOf } from '../platform/resourceTiles'
import { CATALOG_SOURCE_PICKER_ROW } from '../platform/sourcePickerRows'
import { DownSourceSignals } from '../platform/SourceHealthSignal'
import { useMyAccess } from '../platform/useMyAccess'
import { AQSI_KIND, AQSI_MINE } from './aqsiMine'

/** Список зависит от прав: окно, открытое заново, читает его заново, а не из кэша. */
const FRESH_RIGHTS = { refetchOnMountOrArgChange: true } as const

const selectSources = platformMyResources.select(AQSI_MINE.catalog)
const ROW = CATALOG_SOURCE_PICKER_ROW

/**
 * Выбор кабинета для каталога AQSI (закладка `/aqsi/catalog` и «Другой источник» в каталоге): те же плитки, что и в разделах, —
 * общий `ResourcePickerScreen` на «моих ресурсах» (RP-SRC1, Е-3). Список — кабинеты с правом `catalog.read` (кассир без
 * `platform:registry:read` видит свои); выбранный кабинет ЗАМЕНЯЕТ это окно своим окном (rid в адресе).
 */
export function AqsiCatalogSourceBinding() {
  const window = useWindow()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const access = useMyAccess()
  platformMyResources.useQuerySubscription(AQSI_MINE.catalog, FRESH_RIGHTS)
  const result = useAppSelector((state: RootState) => selectSources(state))
  const view = useMemo(() => queryView(result, () => false), [result])
  // Сервер отдаёт только кабинеты (`kind=aqsi`), но 1С в выборе кассового окна не может оказаться даже ошибкой сервера.
  const resources = useMemo(() => (result.data ?? []).filter((resource) => resource.kind === AQSI_KIND), [result.data])
  const tiles = useMemo(() => resources.map((resource) => tileOf(resource, [])), [resources])

  const actions = useMemo<ResourcePickerActions>(
    () => ({
      open: (id) => window.replaceWith(AQSI_WINDOWS.catalog, { rid: id }),
      reload: () => {
        void dispatch(platformMyResources.initiate(AQSI_MINE.catalog, { forceRefetch: true }))
      },
      openResources: () => openWindow(PLATFORM_WINDOWS.resources),
    }),
    [dispatch, openWindow, window],
  )

  return (
    <>
      <DownSourceSignals resources={resources} />
      <ResourcePickerScreen
        vm={{ query: view, tiles, canManageResources: grantsOf(access).registryRead }}
        actions={actions}
        copy={ROW.copy}
        renderButton={ROW.renderButton}
      />
    </>
  )
}
